// src/components/admin/questions/ExamsTab.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Plus, X, Save, Search, Eye, EyeOff, Edit, Trash2,
    FileText, Building2, Calendar, Clock, Upload,
    ChevronLeft, ChevronRight, AlertCircle, CheckCircle, BookLock,
    Lock, Unlock, LayoutList
} from 'lucide-react';
import { questionService } from '../../../services/questionService';
import './ExamsTab.css';

interface ExamsTabProps {
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
    onRegisterNewProva?: (fn: () => void) => void;
    onRegisterImportProva?: (fn: () => void) => void;
    onViewChange?: (isListView: boolean) => void;
}

type ExamsView = 'list' | 'form' | 'import';
type ExamTypeMode = 'prova' | 'simulado';

function stripHtml(html: string): string {
    return html.replace(/<[^>]*>/g, '').trim();
}

// ── Painel de busca de questões ──────────────────────────────────
const QuestionSearchPanel: React.FC<{
    selectedIds: string[];
    onToggle: (id: string) => void;
}> = ({ selectedIds, onToggle }) => {
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('publicado');
    const [difficulty, setDifficulty] = useState('');
    const [questions, setQuestions] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await questionService.getQuestionsAdmin({ search, status, difficulty: difficulty || undefined, page, limit: 15 });
            setQuestions(data.questions || []);
            setTotalPages(data.pagination?.pages ?? 1);
            setTotal(data.pagination?.total ?? 0);
        } catch { /* silencioso */ }
        finally { setLoading(false); }
    }, [search, status, difficulty, page]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setPage(1); }, [search, status, difficulty]);

    return (
        <div className="et-qsearch">
            <div className="et-qsearch-filters">
                <div className="et-qsearch-input-wrap">
                    <Search size={14} />
                    <input type="text" placeholder="Buscar questões..." value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <select value={status} onChange={e => setStatus(e.target.value)}>
                    <option value="">Todos os status</option>
                    <option value="publicado">Publicadas</option>
                    <option value="rascunho">Rascunhos</option>
                    <option value="analise">Em Análise</option>
                </select>
                <select value={difficulty} onChange={e => setDifficulty(e.target.value)}>
                    <option value="">Toda dificuldade</option>
                    <option value="facil">Fácil</option>
                    <option value="medio">Médio</option>
                    <option value="dificil">Difícil</option>
                </select>
                <span className="et-qsearch-total">{total} questão(ões)</span>
            </div>

            {loading ? (
                <div className="et-qsearch-loading">Carregando...</div>
            ) : (
                <div className="et-qsearch-list">
                    {questions.map(q => {
                        const isSelected = selectedIds.includes(q._id);
                        return (
                            <div
                                key={q._id}
                                className={`et-qsearch-item ${isSelected ? 'et-qsearch-item--selected' : ''}`}
                                onClick={() => onToggle(q._id)}
                            >
                                <input type="checkbox" checked={isSelected} readOnly />
                                <div className="et-qsearch-item-body">
                                    <p className="et-qsearch-item-text">{stripHtml(q.statement).substring(0, 120)}...</p>
                                    <div className="et-qsearch-item-meta">
                                        <span className={`badge-difficulty difficulty-${q.difficulty}`}>{q.difficulty}</span>
                                        <span className="et-qsearch-cat">{q.category?.level1}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {totalPages > 1 && (
                <div className="et-qsearch-pagination">
                    <button disabled={page === 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={14} /></button>
                    <span>{page} / {totalPages}</span>
                    <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight size={14} /></button>
                </div>
            )}
        </div>
    );
};

// ── Import Preview ───────────────────────────────────────────────
const ImportPreview: React.FC<{ questions: any[] }> = ({ questions }) => {
    const valid = questions.filter(q => {
        if (!q.statement?.trim()) return false;
        if (!Array.isArray(q.alternatives) || q.alternatives.length < 2) return false;
        return q.alternatives.filter((a: any) => a.isCorrect).length === 1;
    });
    const invalid = questions.length - valid.length;
    const noCategory = valid.filter(q => !q.category?.level1).length;
    const allTags = [...new Set(valid.flatMap(q => q.tags || []))];

    return (
        <div className="et-import-preview">
            <h4 className="et-import-preview-title">Preview das questões</h4>
            <div className="et-import-stats">
                <div className="et-import-stat et-import-stat--ok"><CheckCircle size={16} /><span><strong>{valid.length}</strong> válidas</span></div>
                {invalid > 0 && <div className="et-import-stat et-import-stat--err"><AlertCircle size={16} /><span><strong>{invalid}</strong> inválida{invalid !== 1 ? 's' : ''}</span></div>}
                {noCategory > 0 && <div className="et-import-stat et-import-stat--warn"><AlertCircle size={16} /><span><strong>{noCategory}</strong> sem categoria</span></div>}
                {allTags.length > 0 && <div className="et-import-stat et-import-stat--info"><span>{allTags.length} tag(s)</span></div>}
            </div>
            <div className="et-import-question-list">
                {questions.map((q, i) => {
                    const isValid = valid.includes(q);
                    return (
                        <div key={i} className={`et-import-q-item ${isValid ? '' : 'et-import-q-item--invalid'}`}>
                            <span className="et-import-q-num">{i + 1}</span>
                            <div className="et-import-q-body">
                                <p className="et-import-q-text">{stripHtml(q.statement || '').substring(0, 100) || '(enunciado vazio)'}</p>
                                <div className="et-import-q-meta">
                                    {q.difficulty && <span className={`badge-difficulty difficulty-${q.difficulty}`}>{q.difficulty}</span>}
                                    {q.category?.level1 && <span className="et-import-q-cat">{q.category.level1}</span>}
                                    {!isValid && <span className="et-import-q-err">Inválida</span>}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const ExamsTab: React.FC<ExamsTabProps> = ({ onShowToast, onRegisterNewProva, onRegisterImportProva, onViewChange }) => {
    const [view, setView] = useState<ExamsView>('list');
    const [formType, setFormType] = useState<ExamTypeMode>('prova');

    // ── Estado lista ──────────────────────────────────────────
    const [exams, setExams] = useState<any[]>([]);
    const [loadingExams, setLoadingExams] = useState(false);
    const [editingExamId, setEditingExamId] = useState<string | null>(null);

    // ── Estado formulário manual ──────────────────────────────
    const [examName, setExamName] = useState('');
    const [examInstitution, setExamInstitution] = useState('');
    const [examYear, setExamYear] = useState<number | ''>('');
    const [examArea, setExamArea] = useState('');
    const [examLevel, setExamLevel] = useState('');
    const [examDuration, setExamDuration] = useState(180);
    const [examQuestionIds, setExamQuestionIds] = useState<string[]>([]);
    const [showSearch, setShowSearch] = useState(false);

    // ── Modal de exclusão ─────────────────────────────────────
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
    const [deleteWithQuestions, setDeleteWithQuestions] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // ── Estado importação ─────────────────────────────────────
    const [importName, setImportName] = useState('');
    const [importInstitution, setImportInstitution] = useState('');
    const [importYear, setImportYear] = useState<number | ''>('');
    const [importArea, setImportArea] = useState('');
    const [importLevel, setImportLevel] = useState('');
    const [importDuration, setImportDuration] = useState(180);
    const [importQuestionStatus, setImportQuestionStatus] = useState<'prova' | 'publicado' | 'rascunho'>('prova');
    const [importQuestions, setImportQuestions] = useState<any[] | null>(null);
    const [importFileError, setImportFileError] = useState('');
    const [importing, setImporting] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // ── Carregar lista ────────────────────────────────────────
    const loadExams = useCallback(async () => {
        try {
            setLoadingExams(true);
            const { examService } = await import('../../../services/examService');
            setExams(await examService.getExamsAdmin());
        } catch { onShowToast('Erro ao carregar provas', 'error'); }
        finally { setLoadingExams(false); }
    }, [onShowToast]);

    useEffect(() => { loadExams(); }, [loadExams]);

    useEffect(() => {
        onRegisterNewProva?.(() => { resetForm(); setFormType('prova'); setView('form'); });
        onRegisterImportProva?.(() => { resetImport(); setFormType('prova'); setView('import'); });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { onViewChange?.(view === 'list'); }, [view, onViewChange]);

    const provas = exams.filter(e => e.type !== 'simulado');

    // ── Helpers form manual ───────────────────────────────────
    const resetForm = () => {
        setView('list'); setEditingExamId(null);
        setExamName(''); setExamInstitution('');
        setExamYear(''); setExamArea('');
        setExamLevel(''); setExamDuration(180); setExamQuestionIds([]);
        setShowSearch(false);
    };

    const resetImport = () => {
        setView('list');
        setImportName(''); setImportInstitution(''); setImportYear('');
        setImportArea(''); setImportLevel(''); setImportDuration(180);
        setImportQuestions(null); setImportFileError('');
        setImportQuestionStatus('prova');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSave = async () => {
        if (!examName.trim()) { onShowToast('Nome é obrigatório', 'error'); return; }
        if (formType === 'prova' && !examInstitution.trim()) { onShowToast('Instituição é obrigatória para provas', 'error'); return; }
        if (examQuestionIds.length === 0) { onShowToast('Adicione ao menos uma questão', 'error'); return; }
        try {
            const { examService } = await import('../../../services/examService');
            const data = {
                name: examName,
                institution: examInstitution || undefined,
                year: examYear !== '' ? Number(examYear) : undefined,
                area: examArea || undefined,
                level: examLevel || undefined,
                officialDuration: examDuration,
                questionIds: examQuestionIds,
                type: formType,
            };
            if (editingExamId) {
                await examService.updateExam(editingExamId, data);
                onShowToast(formType === 'simulado' ? 'Simulado atualizado' : 'Prova atualizada', 'success');
            } else {
                await examService.createExam(data);
                onShowToast(formType === 'simulado' ? 'Simulado criado' : 'Prova criada', 'success');
            }
            resetForm(); loadExams();
        } catch (error: any) { onShowToast(error.message || 'Erro ao salvar', 'error'); }
    };

    const handleEdit = async (examId: string) => {
        try {
            const { examService } = await import('../../../services/examService');
            const all = await examService.getExamsAdmin();
            const exam = all.find((e: any) => e._id === examId);
            if (!exam) return;
            setFormType(exam.type === 'simulado' ? 'simulado' : 'prova');
            setEditingExamId(examId); setExamName(exam.name);
            setExamInstitution(exam.institution || '');
            setExamYear(exam.year ?? '');
            setExamArea(exam.area || ''); setExamLevel(exam.level || '');
            setExamDuration(exam.officialDuration);
            setExamQuestionIds((exam.questions || []).map((q: any) => q.questionId?._id || q.questionId));
            setView('form');
        } catch { onShowToast('Erro ao carregar', 'error'); }
    };

    const handleDelete = (exam: any) => {
        setDeleteWithQuestions(false);
        setDeleteTarget({ id: exam._id, name: exam.name });
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            const { examService } = await import('../../../services/examService');
            await examService.deleteExam(deleteTarget.id, deleteWithQuestions);
            onShowToast(deleteWithQuestions ? 'Prova e questões deletadas' : 'Deletado com sucesso', 'success');
            setDeleteTarget(null);
            loadExams();
        } catch { onShowToast('Erro ao deletar', 'error'); }
        finally { setDeleting(false); }
    };

    const handleTogglePublish = async (examId: string) => {
        try {
            const { examService } = await import('../../../services/examService');
            await examService.togglePublish(examId);
            onShowToast('Status alterado', 'success'); loadExams();
        } catch { onShowToast('Erro ao alterar status', 'error'); }
    };

    const handleToggleInterdict = async (examId: string) => {
        try {
            const { examService } = await import('../../../services/examService');
            await examService.toggleInterdict(examId);
            onShowToast('Interdição alterada', 'success'); loadExams();
        } catch { onShowToast('Erro ao interditar', 'error'); }
    };

    // ── Importação via JSON ───────────────────────────────────
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setImportFileError('');
        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const text = ev.target?.result as string;
                const parsed = JSON.parse(text);
                const questions = Array.isArray(parsed) ? parsed : (parsed.questions || []);
                if (!Array.isArray(questions) || questions.length === 0) {
                    setImportFileError('JSON inválido: "questions" deve ser um array não vazio.');
                    return;
                }
                setImportQuestions(questions);
            } catch {
                setImportFileError('Arquivo JSON inválido. Verifique o formato.');
            }
        };
        reader.readAsText(file);
    };

    const handleImport = async () => {
        if (!importName.trim()) { onShowToast('Nome é obrigatório', 'error'); return; }
        if (formType === 'prova' && !importInstitution.trim()) { onShowToast('Instituição é obrigatória para provas', 'error'); return; }
        if (!importQuestions || importQuestions.length === 0) { onShowToast('Carregue um arquivo JSON com questões', 'error'); return; }
        setImporting(true);
        try {
            const { examService } = await import('../../../services/examService');
            const result = await examService.importExam({
                name: importName,
                institution: importInstitution || undefined,
                year: importYear !== '' ? Number(importYear) : undefined,
                area: importArea || undefined,
                level: importLevel || undefined,
                officialDuration: importDuration,
                questions: importQuestions,
                questionStatus: importQuestionStatus,
                type: formType,
            } as any);
            const r = result.results;
            onShowToast(`Importado! ${r.imported} questão(ões)${r.failed > 0 ? `, ${r.failed} falha(s)` : ''}.`, 'success');
            resetImport();
            loadExams();
        } catch (e: any) {
            onShowToast(e.message || 'Erro ao importar', 'error');
        } finally {
            setImporting(false);
        }
    };

    // ── Render card de prova/simulado ─────────────────────────
    const renderExamCard = (exam: any) => (
        <div key={exam._id} className={`exam-admin-card ${exam.interdicted ? 'exam-admin-card--interdicted' : ''}`}>
            <div className="exam-card-header">
                <div className="exam-card-header-left">
                    <span className="et-type-badge et-type-badge--prova"><LayoutList size={11} /> Prova</span>
                    <h4>{exam.name}</h4>
                </div>
                <div className="exam-card-badges">
                    <span className={`badge-status status-${exam.status}`}>{exam.status}</span>
                    {exam.interdicted && <span className="badge-interdicted"><Lock size={10} /> Interditada</span>}
                </div>
            </div>
            <div className="exam-card-info">
                {exam.institution && <div className="info-row"><Building2 size={13} /><span>{exam.institution}</span></div>}
                {exam.year && <div className="info-row"><Calendar size={13} /><span>{exam.year}</span></div>}
                <div className="info-row"><FileText size={13} /><span>{exam.totalQuestions} questões</span></div>
                <div className="info-row"><Clock size={13} /><span>{exam.officialDuration} min</span></div>
            </div>
            <div className="exam-card-actions">
                <button className="btn-action" onClick={() => handleTogglePublish(exam._id)} title={exam.status === 'publicado' ? 'Despublicar' : 'Publicar'}>
                    {exam.status === 'publicado' ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
                <button
                    className={`btn-action ${exam.interdicted ? 'btn-action--active' : ''}`}
                    onClick={() => handleToggleInterdict(exam._id)}
                    title={exam.interdicted ? 'Desinterditar' : 'Interditar'}
                >
                    {exam.interdicted ? <Unlock size={15} /> : <Lock size={15} />}
                </button>
                <button className="btn-action" onClick={() => handleEdit(exam._id)} title="Editar"><Edit size={15} /></button>
                <button className="btn-action btn-delete" onClick={() => handleDelete(exam)} title="Deletar"><Trash2 size={15} /></button>
            </div>
        </div>
    );

    return (
        <div className="qm-block et-wrapper">

            {/* ── Cancelar (quando em formulário/importação) ── */}
            {view !== 'list' && (
                <div className="qm-exams-header">
                    <button className="et-btn-cancel" onClick={view === 'import' ? resetImport : resetForm}>
                        <X size={16} /> Cancelar
                    </button>
                </div>
            )}

            {/* ════════════════════
                VIEW: LISTA
            ════════════════════ */}
            {view === 'list' && (
                <div className="exams-list">
                    {loadingExams ? (
                        <div className="qm-loading">Carregando...</div>
                    ) : exams.length === 0 ? (
                        <div className="qm-empty et-empty-state">
                            <FileText size={48} />
                            <h3>Nenhuma prova cadastrada</h3>
                            <p>Crie uma prova manualmente ou importe um arquivo JSON.</p>
                        </div>
                    ) : (
                        <>
                            {provas.length > 0 && (
                                <div className="et-section-group">
                                    <div className="exams-grid">{provas.map(renderExamCard)}</div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* ════════════════════
                VIEW: FORM MANUAL
            ════════════════════ */}
            {view === 'form' && (
                <div className="exam-form-container et-form-section">
                    <h3 className="et-section-title">
                        {editingExamId
                            ? (formType === 'simulado' ? 'Editar Simulado' : 'Editar Prova')
                            : (formType === 'simulado' ? 'Novo Simulado Graia' : 'Nova Prova')}
                    </h3>
                    <div className="form-grid et-form-grid">
                        <div className="form-group">
                            <label>{formType === 'simulado' ? 'Nome do Simulado *' : 'Nome da Prova *'}</label>
                            <input type="text" value={examName} onChange={e => setExamName(e.target.value)} placeholder={formType === 'simulado' ? 'Ex: Simulado Cardiologia #1' : 'Ex: FAMEMA 2024'} />
                        </div>
                        {formType === 'prova' && (
                            <div className="form-group">
                                <label>Instituição *</label>
                                <input type="text" value={examInstitution} onChange={e => setExamInstitution(e.target.value)} placeholder="Ex: FAMEMA" />
                            </div>
                        )}
                        <div className="form-group">
                            <label>Ano (opcional)</label>
                            <input type="number" value={examYear} onChange={e => setExamYear(e.target.value === '' ? '' : Number(e.target.value))} min={2000} max={2100} placeholder="Ex: 2024" />
                        </div>
                        <div className="form-group"><label>Área (opcional)</label><input type="text" value={examArea} onChange={e => setExamArea(e.target.value)} placeholder="Ex: Ciências da Natureza" /></div>
                        <div className="form-group"><label>Nível (opcional)</label><input type="text" value={examLevel} onChange={e => setExamLevel(e.target.value)} placeholder="Ex: Residência Médica" /></div>
                        <div className="form-group"><label>Duração (minutos) *</label><input type="number" value={examDuration} onChange={e => setExamDuration(Number(e.target.value))} min={1} /></div>
                    </div>

                    <div className="form-group full-width" style={{ marginTop: 16 }}>
                        <label>Questões ({examQuestionIds.length} selecionadas)</label>
                        <button className="btn-add-questions" onClick={() => setShowSearch(v => !v)}>
                            <Plus size={15} /> {showSearch ? 'Fechar busca' : 'Buscar e adicionar questões'}
                        </button>
                        {examQuestionIds.length > 0 && (
                            <div className="et-selected-ids">
                                {examQuestionIds.map((id, i) => (
                                    <div key={id} className="et-selected-id-item">
                                        <span>{i + 1}. {id.slice(-8)}</span>
                                        <button onClick={() => setExamQuestionIds(examQuestionIds.filter(x => x !== id))}><X size={12} /></button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {showSearch && (
                        <QuestionSearchPanel
                            selectedIds={examQuestionIds}
                            onToggle={(id) => setExamQuestionIds(prev =>
                                prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
                            )}
                        />
                    )}

                    <div className="form-actions">
                        <button className="btn-cancel et-btn-cancel" onClick={resetForm}><X size={16} /> Cancelar</button>
                        <button className="btn-save-exam et-btn-save" onClick={handleSave}>
                            <Save size={16} /> {editingExamId ? 'Atualizar' : (formType === 'simulado' ? 'Criar Simulado' : 'Criar Prova')}
                        </button>
                    </div>
                </div>
            )}

            {/* ════════════════════
                VIEW: IMPORTAR JSON
            ════════════════════ */}
            {view === 'import' && (
                <div className="et-import-container">
                    <h3 className="et-section-title">
                        <Upload size={18} /> {formType === 'simulado' ? 'Importar Simulado via JSON' : 'Importar Prova via JSON'}
                    </h3>
                    <p className="et-import-desc">
                        Importe um arquivo JSON no formato padrão GRAIA. As questões serão criadas automaticamente.
                    </p>

                    <div className="form-grid et-form-grid">
                        <div className="form-group">
                            <label>{formType === 'simulado' ? 'Nome do Simulado *' : 'Nome da Prova *'}</label>
                            <input type="text" value={importName} onChange={e => setImportName(e.target.value)} placeholder={formType === 'simulado' ? 'Ex: Simulado Cardiologia #1' : 'Ex: ENADE 2023'} />
                        </div>
                        {formType === 'prova' && (
                            <div className="form-group">
                                <label>Instituição *</label>
                                <input type="text" value={importInstitution} onChange={e => setImportInstitution(e.target.value)} placeholder="Ex: ENADE, REVALIDA" />
                            </div>
                        )}
                        <div className="form-group">
                            <label>Ano (opcional)</label>
                            <input type="number" value={importYear} onChange={e => setImportYear(e.target.value === '' ? '' : Number(e.target.value))} min={2000} max={2100} placeholder="Ex: 2024" />
                        </div>
                        <div className="form-group"><label>Área (opcional)</label><input type="text" value={importArea} onChange={e => setImportArea(e.target.value)} placeholder="Ex: Medicina" /></div>
                        <div className="form-group"><label>Nível (opcional)</label><input type="text" value={importLevel} onChange={e => setImportLevel(e.target.value)} placeholder="Ex: Residência Médica" /></div>
                        <div className="form-group"><label>Duração (minutos)</label><input type="number" value={importDuration} onChange={e => setImportDuration(Number(e.target.value))} min={1} /></div>
                    </div>

                    <div className="et-import-status-group">
                        <label className="et-import-status-label">Visibilidade das questões importadas:</label>
                        <div className="et-import-status-options">
                            <label className={`et-status-option ${importQuestionStatus === 'prova' ? 'et-status-option--active' : ''}`}>
                                <input type="radio" name="qstatus" value="prova" checked={importQuestionStatus === 'prova'} onChange={() => setImportQuestionStatus('prova')} />
                                <BookLock size={15} />
                                <div>
                                    <strong>Exclusiva da prova</strong>
                                    <span>Não aparece no banco de questões.</span>
                                </div>
                            </label>
                            <label className={`et-status-option ${importQuestionStatus === 'publicado' ? 'et-status-option--active' : ''}`}>
                                <input type="radio" name="qstatus" value="publicado" checked={importQuestionStatus === 'publicado'} onChange={() => setImportQuestionStatus('publicado')} />
                                <Eye size={15} />
                                <div>
                                    <strong>Publicada (banco público)</strong>
                                    <span>Aparece no banco e na prova.</span>
                                </div>
                            </label>
                            <label className={`et-status-option ${importQuestionStatus === 'rascunho' ? 'et-status-option--active' : ''}`}>
                                <input type="radio" name="qstatus" value="rascunho" checked={importQuestionStatus === 'rascunho'} onChange={() => setImportQuestionStatus('rascunho')} />
                                <EyeOff size={15} />
                                <div>
                                    <strong>Rascunho</strong>
                                    <span>Salva como rascunho.</span>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="et-import-upload-area">
                        <input ref={fileInputRef} type="file" accept=".json" onChange={handleFileChange} style={{ display: 'none' }} id="exam-json-upload" />
                        <label htmlFor="exam-json-upload" className="et-upload-label">
                            <Upload size={22} />
                            <span>{importQuestions ? `${importQuestions.length} questão(ões) carregada(s)` : 'Clique para selecionar o arquivo JSON'}</span>
                        </label>
                        {importFileError && <p className="et-import-file-error"><AlertCircle size={13} /> {importFileError}</p>}
                    </div>

                    {importQuestions && importQuestions.length > 0 && <ImportPreview questions={importQuestions} />}

                    <div className="form-actions">
                        <button className="btn-cancel et-btn-cancel" onClick={resetImport}><X size={16} /> Cancelar</button>
                        <button
                            className="btn-save-exam et-btn-save"
                            onClick={handleImport}
                            disabled={importing || !importQuestions || !importName.trim() || (formType === 'prova' && !importInstitution.trim())}
                        >
                            <Upload size={16} />
                            {importing ? 'Importando...' : `Importar${importQuestions ? ` (${importQuestions.length} questões)` : ''}`}
                        </button>
                    </div>
                </div>
            )}

            {/* ── Modal de exclusão ── */}
            {deleteTarget && (
                <div className="et-modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
                    <div className="et-modal" onClick={e => e.stopPropagation()}>
                        <div className="et-modal-header">
                            <div className="et-modal-icon"><Trash2 size={20} /></div>
                            <div className="et-modal-header-text">
                                <h3 className="et-modal-title">Excluir prova</h3>
                                <p className="et-modal-desc">
                                    Tem certeza que deseja excluir <strong>{deleteTarget.name}</strong>?
                                </p>
                            </div>
                        </div>
                        <div className="et-modal-divider" />
                        <label className="et-modal-checkbox">
                            <input type="checkbox" checked={deleteWithQuestions} onChange={e => setDeleteWithQuestions(e.target.checked)} disabled={deleting} />
                            <span>Também excluir as questões desta prova</span>
                        </label>
                        {deleteWithQuestions && (
                            <p className="et-modal-warn">As questões vinculadas serão permanentemente deletadas.</p>
                        )}
                        <div className="et-modal-actions">
                            <button className="et-modal-btn et-modal-btn--cancel" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancelar</button>
                            <button className="et-modal-btn et-modal-btn--delete" onClick={handleDeleteConfirm} disabled={deleting}>
                                {deleting ? 'Excluindo...' : 'Sim, excluir'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExamsTab;
